import { useEffect } from 'react';

import { cn } from '@utils/cn';

import { useAnimatedMount } from '@ui-component-library/hooks/useAnimatedMount';

import {
  sheetBackdropClasses,
  sheetContentClasses,
  sheetContentWrapperStyles,
  sheetFooterClasses,
  sheetHeaderClasses
} from './Sheet.styles';
import type {
  SheetContentProps,
  SheetFooterProps,
  SheetHeaderProps,
  SheetProps
} from './Sheet.types';

const CLOSED_TRANSFORM: Record<string, string> = {
  right: 'translateX(100%)',
  left: 'translateX(-100%)',
  bottom: 'translate(-50%, -50%)'
};

const OPEN_TRANSFORM: Record<string, string> = {
  right: 'translateX(0)',
  left: 'translateX(0)',
  bottom: 'translate(-50%, 0)'
};

export const Sheet = ({
  isOpen,
  onClose,
  side = 'right',
  children,
  className,
  hideBackdrop = false,
  ...props
}: SheetProps) => {
  const { shouldRender, animateIn, ref, onTransitionEnd } = useAnimatedMount(isOpen);

  // Close on ESC key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={cn(sheetBackdropClasses({ hideBackdrop }))}
      style={{
        opacity: animateIn ? 1 : 0,
        visibility: shouldRender ? 'visible' : 'hidden',
        transition: animateIn
          ? 'opacity 300ms ease-in-out'
          : 'opacity 200ms ease-in-out 100ms, visibility 0ms 300ms'
      }}
      onClick={() => onClose()}
    >
      <div
        ref={ref}
        className={cn(sheetContentClasses({ side }), className)}
        style={{
          transform: animateIn ? OPEN_TRANSFORM[side] : CLOSED_TRANSFORM[side],
          transition: 'transform 300ms ease-in-out'
        }}
        onTransitionEnd={onTransitionEnd}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

const SheetHeader = ({ children, className, ...props }: SheetHeaderProps) => (
  <div className={cn(sheetHeaderClasses(), className)} {...props}>
    {children}
  </div>
);

const SheetContent = ({ children, className, ...props }: SheetContentProps) => (
  <div className={cn(sheetContentWrapperStyles(), className)} {...props}>
    {children}
  </div>
);

const SheetFooter = ({ children, className, ...props }: SheetFooterProps) => (
  <div className={cn(sheetFooterClasses(), className)} {...props}>
    {children}
  </div>
);

Sheet.Header = SheetHeader;
Sheet.Content = SheetContent;
Sheet.Footer = SheetFooter;
